/**
 * Supabase Client (compatibility shim -> single canonical client).
 *
 * There must be exactly ONE browser Supabase auth client. Two clients created
 * with autoRefreshToken both acquire the same navigator.locks mutex
 * ("lock:sb-<ref>-auth-token") and steal it from one another, which surfaces as
 * intermittent false 401 "sign in again" logouts for authenticated users
 * ("Lock ... was released because another request stole it").
 *
 * This module historically created a SECOND, separate client (and read a
 * different env var name for the anon key). It now delegates to the single
 * canonical client in @/integrations/supabase/client, so every importer shares
 * ONE auth client and ONE refresh lock. As a bonus, callers here inherit that
 * client's runtime self-heal (ensureSupabaseConfig), so a browser bundle built
 * without the baked Supabase config still repairs itself before auth calls.
 *
 * Kept as a thin shim (not deleted) because ~30 modules import from here; the
 * exports below preserve the exact surface those callers expect.
 */

import type { SupabaseClient } from "@supabase/supabase-js";
import { supabase as canonicalClient, ensureSupabaseConfig } from "@/integrations/supabase/client";

/** The one canonical browser/server Supabase client. */
export function getSupabase(): SupabaseClient {
  return canonicalClient as unknown as SupabaseClient;
}

/** The one canonical Supabase client instance (same object as getSupabase()). */
export const supabase = canonicalClient as unknown as SupabaseClient;

/** Re-exported so callers that only imported from here can trigger the self-heal. */
export { ensureSupabaseConfig };
